import React, { PropsWithChildren, useState } from 'react';
import { css } from '@emotion/react';
import { i18n_all_content } from '@yi/i18n/phrases/i18n_all_content';
import { i18n_all_rights_reserved } from '@yi/i18n/phrases/i18n_all_rights_reserved';
import { i18n_be_first_to_hear_about_exclusive_offers } from '@yi/i18n/phrases/i18n_be_first_to_hear_about_exclusive_offers';
import { i18n_copyright } from '@yi/i18n/phrases/i18n_copyright';
import { i18n_thank_you_for_being_part_of_yoga_international_family } from '@yi/i18n/phrases/i18n_thank_you_for_being_part_of_yoga_international_family';

import { useCurrentUser } from '~/src/common/user';
import { useIsSSR } from '~/src/components/hooks/useIsSSR';
import { useLocale } from '~/src/components/LocaleProvider';
import { typography } from '~/src/styles/design-system';
import { breakpoints, printHidden } from '~/src/styles/styled-mixins';
import { fontWeight, sideNavWidth } from '~/src/styles/styled-variables';

import { Form, BUTTON_HEIGHT } from './Form';
import { Links } from './Links';
import { SimpleFooter } from './SimpleFooter';
import { SocialLinks } from './SocialLinks';

interface Props {
  kind?: 'simpleFooter' | 'hideFooter' | 'hideFooterMobile';
}
const GRID_AREA = {
  links: 'links',
  copyright: 'copyright',
  form: 'form',
  social: 'social',
  text: 'text',
};

const textStyle = css(typography.mobileBodyTextBold, {
  gridArea: GRID_AREA.text,
  color: 'var(--Pure-White, #FFF)',
  [breakpoints.minDesktop]: {
    alignSelf: 'start',
    lineHeight: BUTTON_HEIGHT,
  },
});
const styles = {
  wrapper: css([
    printHidden,
    {
      background: 'var(--Navigation-Background)',
      [breakpoints.minDesktopLg]: {
        paddingLeft: sideNavWidth,
      },
    },
  ]),
  container: css([
    {
      padding: '56px 0 40px',
      display: 'grid',
      gridTemplateAreas: `'. ${GRID_AREA.links} .' '. ${GRID_AREA.text} .' '. ${GRID_AREA.form} .' '. ${GRID_AREA.social} .' '. ${GRID_AREA.copyright} .'`,
      rowGap: '48px',
      gridTemplateColumns: '25px auto 25px',
      [breakpoints.minTablet]: {
        gridTemplateColumns: '1fr 340px 1fr',
        rowGap: '32px',
      },
      [breakpoints.minDesktop]: {
        padding: '120px 0 64px',
        gridTemplateAreas: `'. ${GRID_AREA.links} ${GRID_AREA.links} ${GRID_AREA.links} .' '. ${GRID_AREA.text} . ${GRID_AREA.form} .' '. ${GRID_AREA.social} ${GRID_AREA.social} ${GRID_AREA.social} .' '. ${GRID_AREA.copyright} ${GRID_AREA.copyright} ${GRID_AREA.copyright} .'`,
        gridTemplateColumns: '25px auto 48px 1fr 25px',
        rowGap: '80px',
        placeItems: 'center',
        justifyContenr: 'center',
        width: 'min(1200px,100% - 2 * 25px)',
        margin: 'auto',
      },
    },
  ]),
  containerWithoutForm: css({
    gridTemplateAreas: `'. ${GRID_AREA.links} .' '. ${GRID_AREA.text} .' '. ${GRID_AREA.social} .' '. ${GRID_AREA.copyright} .'`,
  }),
  hideFooterOnMobile: css({
    display: 'none',
    [breakpoints.minTabletLandscape]: {
      display: 'block',
    },
  }),
  links: css({
    gridArea: GRID_AREA.links,
  }),
  copyright: css({
    gridArea: GRID_AREA.copyright,
    fontFamily: 'Montserrat',
    fontWeight: fontWeight.regular,
    color: 'var(--Pure-White, #FFF)',
    fontSize: '12px',
    fontStyle: 'normal',
    lineHeight: 'normal',
  }),
  form: css({
    gridArea: GRID_AREA.form,
  }),
  social: css({
    gridArea: GRID_AREA.social,
  }),
  text: textStyle,
  greetingText: css(textStyle, {
    [breakpoints.minDesktop]: {
      gridColumn: '2/5',
    },
  }),
};

export const Footer: React.FC<PropsWithChildren<Props>> = ({ kind }) => {
  const { locale } = useLocale();
  const { isLoggedIn, loading } = useCurrentUser();
  const [isFormSubmitted, setIsFormSubmitted] = useState<boolean>(false);

  const isSSR = useIsSSR();
  const isUnauthorized = !isLoggedIn && !loading;
  const isFormShown = isSSR || (isUnauthorized && !isFormSubmitted);

  if (kind === 'hideFooter') return null;
  if (kind === 'simpleFooter') return <SimpleFooter />;

  return (
    <footer
      data-testid="main-footer"
      css={[styles.wrapper, kind === 'hideFooterMobile' ? styles.hideFooterOnMobile : {}]}
    >
      <div css={[styles.container, !isFormShown && styles.containerWithoutForm]}>
        <Links css={styles.links} />
        {isFormShown ? (
          <>
            <div css={styles.text}>{i18n_be_first_to_hear_about_exclusive_offers(locale)}</div>
            <Form
              css={styles.form}
              onSubmit={() => {
                setIsFormSubmitted(true);
              }}
            />
          </>
        ) : (
          <div css={styles.greetingText}>
            {i18n_thank_you_for_being_part_of_yoga_international_family(locale)}
          </div>
        )}
        <SocialLinks css={styles.social} />
        <div css={styles.copyright}>
          {`${i18n_all_content(locale)} © ${i18n_copyright(
            locale,
          )} ${new Date().getFullYear()}. ${i18n_all_rights_reserved(locale)}.`}
        </div>
      </div>
    </footer>
  );
};
